import config from './config.js';

// Global state
const state = {
    supabaseClient: null,
    user: null,
    priceData: null,
    updateInterval: null
};

// Helper Functions
function showAlert(type, message) {
    const alertDiv = document.createElement('div');
    alertDiv.className = `alert alert-${type} alert-dismissible fade show m-3`;
    alertDiv.innerHTML = `
        <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2"></i>
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    document.body.insertBefore(alertDiv, document.body.firstChild);
    
    setTimeout(() => alertDiv.remove(), 5000);
}

function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString();
}

function formatTransactionId(id) {
    if (!id) return 'N/A';
    return `<span class="transaction-id" role="button" title="Click to copy" data-id="${id}">${id.slice(0, 8)}...</span>`;
}

// Load Functions
async function loadPriceHistory() {
    try {
        const { data: prices, error } = await state.supabaseClient
            .from('price_history')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(100);

        if (error) throw error;

        const priceHistoryBody = document.getElementById('price-history-body');
        if (!priceHistoryBody) return;

        priceHistoryBody.innerHTML = prices.map(price => `
            <tr>
                <td>${formatDate(price.created_at)}</td>
                <td>$${price.base_price.toFixed(2)}</td>
                <td>$${price.current_price.toFixed(2)}</td>
                <td>$${price.last_paid_price.toFixed(2)}</td>
                <td>${price.decay_rate}</td>
            </tr>
        `).join('');

        // Update current price display if it exists
        const currentPriceElement = document.getElementById('current-price');
        if (currentPriceElement && prices.length > 0) {
            currentPriceElement.textContent = prices[0].current_price.toFixed(2);
        }

        state.priceData = prices[0];

    } catch (error) {
        console.error('Error loading price history:', error);
        showAlert('danger', 'Failed to load price history');
    }
}

async function loadUserStats() {
    try {
        console.log('Loading user stats...');
        
        // Get all successful payments
        const { data: payments, error: paymentsError } = await state.supabaseClient
            .from('payments')
            .select('amount, created_at, status')
            .eq('status', 'succeeded');

        if (paymentsError) {
            console.error('Error loading payments:', paymentsError);
            return;
        }

        console.log('Fetched payments:', payments);

        // Get current date values
        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        console.log('Date ranges:', {
            now: now.toISOString(),
            startOfYear: startOfYear.toISOString(),
            startOfMonth: startOfMonth.toISOString(),
            startOfWeek: startOfWeek.toISOString(),
            startOfDay: startOfDay.toISOString()
        });

        // Calculate revenues for different periods
        const revenues = payments?.reduce((acc, payment) => {
            const paymentDate = new Date(payment.created_at);
            const amount = parseFloat(payment.amount) || 0;

            console.log('Processing payment:', {
                date: payment.created_at,
                amount: amount,
                status: payment.status
            });

            if (paymentDate >= startOfYear) {
                acc.ytd += amount;
            }
            if (paymentDate >= startOfMonth) {
                acc.mtd += amount;
            }
            if (paymentDate >= startOfWeek) {
                acc.wtd += amount;
            }
            if (paymentDate >= startOfDay) {
                acc.today += amount;
            }

            return acc;
        }, { ytd: 0, mtd: 0, wtd: 0, today: 0 }) || { ytd: 0, mtd: 0, wtd: 0, today: 0 };

        console.log('Calculated revenues:', revenues);

        // Get total number of ads and advertisers
        const { data: adStats, error: adsError } = await state.supabaseClient
            .from('ads')
            .select('user_id, price_paid');

        if (adsError) {
            console.error('Error loading ads:', adsError);
            return;
        }

        console.log('Fetched ads:', adStats);

        // Calculate unique advertisers
        const uniqueAdvertisers = new Set(adStats?.map(ad => ad.user_id) || []).size;
        const totalAds = adStats?.length || 0;

        // If no payments found, use price_paid from ads as fallback
        if (!payments || payments.length === 0) {
            console.log('No payments found, using price_paid from ads');
            const adRevenues = adStats?.reduce((acc, ad) => {
                const amount = parseFloat(ad.price_paid) || 0;
                acc.ytd += amount;
                acc.mtd += amount;
                acc.wtd += amount;
                acc.today += amount;
                return acc;
            }, { ytd: 0, mtd: 0, wtd: 0, today: 0 }) || { ytd: 0, mtd: 0, wtd: 0, today: 0 };
            
            console.log('Calculated ad revenues:', adRevenues);
            Object.assign(revenues, adRevenues);
        }

        // Update UI
        document.getElementById('ytd-revenue').textContent = revenues.ytd.toFixed(2);
        document.getElementById('mtd-revenue').textContent = revenues.mtd.toFixed(2);
        document.getElementById('wtd-revenue').textContent = revenues.wtd.toFixed(2);
        document.getElementById('today-revenue').textContent = revenues.today.toFixed(2);
        document.getElementById('total-ads').textContent = totalAds;
        document.getElementById('total-advertisers').textContent = uniqueAdvertisers;

        console.log('UI updated with revenues:', {
            ytd: revenues.ytd.toFixed(2),
            mtd: revenues.mtd.toFixed(2),
            wtd: revenues.wtd.toFixed(2),
            today: revenues.today.toFixed(2),
            totalAds,
            uniqueAdvertisers
        });

    } catch (error) {
        console.error('Error loading user stats:', error);
        showAlert('danger', 'Failed to load user statistics');
    }
}

async function loadAdStats() {
    try {
        const { data: ads, error: adsError } = await state.supabaseClient
            .from('ads')
            .select(`
                *,
                payments (
                    amount,
                    status,
                    stripe_payment_id
                )
            `)
            .order('created_at', { ascending: false });

        if (adsError) {
            console.error('Error loading ads:', adsError);
            return;
        }

        const tableBody = document.getElementById('admin-ads-list');
        if (!tableBody) {
            console.error('Could not find admin-ads-list element');
            return;
        }

        if (!ads || ads.length === 0) {
            tableBody.innerHTML = '<tr><td colspan="8" class="text-center">No ads found</td></tr>';
            return;
        }

        tableBody.innerHTML = ads.map(ad => {
            const payment = ad.payments?.[0];
            const transactionId = payment?.stripe_payment_id || 'N/A';
            return `
                <tr>
                    <td>${ad.text}</td>
                    <td><a href="${ad.url}" target="_blank">${ad.url}</a></td>
                    <td>${ad.user_id}</td>
                    <td>$${payment?.amount?.toFixed(2) || '0.00'}</td>
                    <td>${formatDate(ad.created_at)}</td>
                    <td>${formatTransactionId(transactionId)}</td>
                    <td><span class="badge ${ad.active ? 'bg-success' : 'bg-secondary'}">${ad.active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                        <button class="btn btn-sm btn-${ad.active ? 'warning' : 'success'}" onclick="toggleAdStatus('${ad.id}', ${ad.active})">
                            <i class="fas fa-${ad.active ? 'pause' : 'play'}"></i>
                        </button>
                    </td>
                </tr>
            `;
        }).join('');

    } catch (error) {
        console.error('Error loading ad stats:', error);
        showAlert('danger', 'Failed to load ad statistics');
    }
}

// Event Listeners
function setupEventListeners() {
    // Logout button
    const logoutBtn = document.getElementById('logout-btn');
    if (logoutBtn) {
        logoutBtn.addEventListener('click', async () => {
            try {
                const { error } = await state.supabaseClient.auth.signOut();
                if (error) throw error;
                window.location.href = '/';
            } catch (error) {
                console.error('Error logging out:', error);
                showAlert('danger', 'Failed to log out');
            }
        });
    }

    // View Profile button
    const viewProfileBtn = document.getElementById('view-profile-btn');
    if (viewProfileBtn) {
        viewProfileBtn.addEventListener('click', (e) => {
            e.preventDefault();
            window.location.href = '/profile.html';
        });
    }

    // My Ads button
    const myAdsBtn = document.getElementById('my-ads-btn');
    if (myAdsBtn) {
        myAdsBtn.addEventListener('click', (e) => {
            e.preventDefault();
            window.location.href = '/profile.html#my-ads';
        });
    }

    // Price update form
    const updatePriceForm = document.getElementById('update-price-form');
    if (updatePriceForm) {
        updatePriceForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            
            const basePrice = parseFloat(document.getElementById('base-price').value);
            const decayRate = parseFloat(document.getElementById('decay-rate').value);
            
            if (isNaN(basePrice) || isNaN(decayRate)) {
                showAlert('danger', 'Please enter valid numbers');
                return;
            }
            
            try {
                const { error } = await state.supabaseClient
                    .from('price_history')
                    .insert([{
                        base_price: basePrice,
                        current_price: basePrice,
                        last_paid_price: state.priceData?.last_paid_price || basePrice,
                        decay_rate: decayRate
                    }]);

                if (error) throw error;

                showAlert('success', 'Price updated successfully');
                await loadPriceHistory();
                updatePriceForm.reset();

            } catch (error) {
                console.error('Error updating price:', error);
                showAlert('danger', 'Failed to update price');
            }
        });
    }

    // Search functionality
    const searchInput = document.getElementById('search-ads');
    if (searchInput) {
        let debounceTimeout;
        searchInput.addEventListener('input', (e) => {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
                const searchTerm = e.target.value.toLowerCase();
                const rows = document.querySelectorAll('#ads-table-body tr');
                
                rows.forEach(row => {
                    const text = row.textContent.toLowerCase();
                    row.style.display = text.includes(searchTerm) ? '' : 'none';
                });
            }, 300);
        });
    }
}

// Initialize admin panel
export async function init() {
    try {
        console.log('Initializing admin panel...');
        
        // Get Supabase client from window
        state.supabaseClient = window.supabaseClient;
        if (!state.supabaseClient) {
            throw new Error('Supabase client not initialized');
        }
        console.log('Supabase client stored in state');

        // Get current session
        const { data: { session }, error: sessionError } = await state.supabaseClient.auth.getSession();
        if (sessionError) {
            console.error('Session error:', sessionError);
            return;
        }

        if (!session) {
            console.log('No session found');
            return;
        }

        // Store user in state
        state.user = session.user;
        console.log('User stored in state:', state.user.email);
        
        // Set admin email in header
        const adminEmailElement = document.getElementById('admin-email');
        if (adminEmailElement) {
            adminEmailElement.textContent = state.user.email;
        }

        // Verify admin status one more time
        const { data: adminData, error: adminError } = await state.supabaseClient
            .from('administrators')
            .select('*')
            .eq('user_id', state.user.id)
            .single();

        if (adminError || !adminData) {
            console.error('Admin verification failed:', adminError);
            return;
        }

        console.log('Admin status verified');

        // Load initial data
        await Promise.all([
            loadPriceHistory(),
            loadUserStats(),
            loadAdStats()
        ]);

        // Set up event listeners
        setupEventListeners();

        // Set up auto-refresh
        state.updateInterval = setInterval(async () => {
            await Promise.all([
                loadPriceHistory(),
                loadUserStats(),
                loadAdStats()
            ]);
        }, 30000);

        // Set up cleanup on page unload
        window.addEventListener('unload', () => {
            if (state.updateInterval) {
                clearInterval(state.updateInterval);
            }
        });

        console.log('Admin panel initialized successfully');
    } catch (error) {
        console.error('Error initializing admin panel:', error);
        showAlert('danger', 'Failed to initialize admin panel');
    }
}

// Add toggleAdStatus function to window object
window.toggleAdStatus = async function(adId, currentlyActive) {
    try {
        const { error } = await state.supabaseClient
            .from('ads')
            .update({ active: !currentlyActive })
            .eq('id', adId);

        if (error) throw error;

        showAlert('success', `Ad ${currentlyActive ? 'disabled' : 'enabled'} successfully`);
        await loadAdStats();
    } catch (error) {
        console.error('Error toggling ad status:', error);
        showAlert('danger', 'Failed to update ad status');
    }
};